<template>
  <div class="top-bar">
    <div class="left-section">
      <div class="internal-logo-wrapper">
        <div class="customer-logo-background">
          <img src="../assets/customer_logo.webp" alt="Customer Logo" class="top-bar-logo customer-logo" /> 
        </div>
      </div>
      <img v-if="currentApp?.iconUrl" :src="currentApp.iconUrl" alt="App Icon" class="top-bar-logo app-icon" />
      <span v-if="currentApp?.name" class="app-title">{{ currentApp.name }}</span>
    </div>
    <div class="center-section">
      <!-- Timestamp moved to production scheduler header controls -->
    </div>
    <div class="right-section">
      <div class="account-icon-container" @click="toggleUserMenu">
        <img src="../assets/account.svg" alt="Account" class="account-icon" />
        <div v-if="showUserMenu" class="user-menu">
          <div v-if="user" class="user-info">
            Signed in as: {{ user.attributes?.email || user.username }}
          </div>
          <button @click="handleSignOut" class="sign-out-button">Sign Out</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useAppStore } from '../stores/appStore'; // Import the store

const appStore = useAppStore();
const currentApp = computed(() => appStore.currentApp);
const showUserMenu = ref(false);

// Props to receive user object and signOut function from Amplify Authenticator
const props = defineProps({
  user: {
    type: Object,
    default: null
  },
  signOut: {
    type: Function,
    default: () => {}
  }
});

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value;
};

const handleSignOut = () => {
  if (props.signOut) {
    props.signOut(); // Call the signOut function passed from App.vue
  }
  showUserMenu.value = false; // Close menu after sign out
};

// Props are no longer needed for appTitle and appIconUrl
// defineProps({
//   appTitle: {
//     type: String,
//     default: ''
//   },
//   appIconUrl: {
//     type: String,
//     default: ''
//   }
// });
</script>

<style scoped>
.top-bar {
  height: var(--global-header-height, 60px);
  background-color: #5E6977;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  position: relative;
}

.left-section {
  display: flex;
  align-items: center;
}

.center-section {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.right-section {
  display: flex;
  align-items: center;
  position: relative; /* For positioning the user menu */
}

.top-bar-logo {
  /* Removed height and width from here as it's too general,
     specific logos will define their own sizes or their wrappers will. */
  /* height: 60px; */
  /* width: auto; */
}

.internal-logo-wrapper {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0px; 
}

.customer-logo-background {
  width: 100%; /* Fills internal-logo-wrapper */
  height: 100%; /* Fills internal-logo-wrapper */
  /* background-color: #F5F6F7; */ /* REMOVED light background */
  padding: 5px; /* Padding inside the background for the image - this will now be transparent padding */
  /* border-radius: 30px; */ /* Makes the 60x60 background circular - REMOVED for square */
  box-sizing: border-box;
  display: flex; /* To center the img tag (customer-logo) */
  align-items: center;
  justify-content: center;
}

.app-icon {
  height: 30px; 
  width: 30px; 
  margin-left: 30px; /* Changed to 30px */
}

.app-title {
  font-size: 1.2em; 
  color: #dce3ee; 
  margin-left: 10px;
  font-weight: bold;
}

.customer-logo {
  max-height: 100%; /* Image fits within the padded .customer-logo-background */
  max-width: 100%;
  object-fit: contain;
  opacity: 0.5; /* Reduced opacity further */
  /* No background, padding, or border-radius for the img itself */
}

.account-icon-container {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  position: relative; /* Context for the dropdown */
}

.account-icon-container:hover {
  background-color: #4A5460;
}

.account-icon {
  height: 20px;
  width: 20px;
}

.user-menu {
  position: absolute;
  top: 100%; /* Position below the account icon */
  right: 0;
  background-color: #fff; /* White background for the menu */
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 10px;
  z-index: 1000; /* Ensure it's above other content */
  min-width: 200px; /* Minimum width for the menu */
  color: #333; /* Darker text color for readability */
}

.user-info {
  margin-bottom: 10px;
  font-size: 0.9em;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.sign-out-button {
  background-color: #5E6977; /* Match header button style */
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  width: 100%;
  text-align: left;
}

.sign-out-button:hover {
  background-color: #4A5460; /* Darken on hover */
}

/* Timestamp display styles */
.timestamp-container {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.timestamp-text {
  font-size: 0.85em;
  color: #dce3ee;
  white-space: nowrap;
  min-width: 200px;
  text-align: center;
}

.timestamp-loading {
  opacity: 0.7;
  font-style: italic;
}

.timestamp-error {
  color: #ff9999;
}

.refresh-button {
  background: none;
  border: none;
  color: #dce3ee;
  cursor: pointer;
  padding: 2px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: 0.7;
}

.refresh-button:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.1);
  transform: rotate(180deg);
}

.refresh-button:active {
  transform: rotate(180deg) scale(0.9);
}
</style> 